import React, { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import style from './MainProfile.module.css'
import LocationIcon2 from '../../../../assets/svgs/Location2'
import About from './About'
import Experience from './Experience'
import Education from './Education'
import Skills from './Skills'
import Languages from './Languages'
import { useLocation } from 'react-router-dom'
import { useLayoutContext } from '../../../../contexts/LayoutContext'

import EditIcon from '../../../../assets/svgs/EditIcon'
import useWindowWidth from '../../../../hooks/useWindowWidth'
import ProfileLayout from '../components/ProfileLayout'

const urlLocal = process.env.REACT_APP_API_URL || ''

const CameraIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="13" r="4" stroke="#fff" strokeWidth="2"/>
  </svg>
)

const ImageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2"/>
    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
    <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
}

const MainProfile = ({
  userProfile,
  toggleModal,
  openModal,
  owner,
  editMode,
  editModelToggle,
}) => {

  const [profileImage, setProfileImage] = useState(null)
  const [coverSrc, setCoverSrc] = useState(null)
  const [uploading, setUploading] = useState(false)
  const profileInputRef = useRef(null)
  const coverInputRef = useRef(null)

  const location = useLocation()
  const { layout, fetchProfileLayout } = useLayoutContext()
  const userId = new URLSearchParams(location.search).get('id')
  const innerWidth = useWindowWidth()

  useEffect(() => {
    fetchProfileLayout(userId)
  }, [])

  // Sync if userProfile loads after mount
  const avatarUrl = (uid, type) => `${urlLocal}/users/avatar/${uid}/${type}`

  useEffect(() => {
    const uid = userProfile?.user?.user_id
    if (!uid) return
    if (userProfile?.user?.profile_picture_url) {
      setProfileImage(avatarUrl(uid, 'profile'))
    }
    if (userProfile?.user?.cover_image_url) {
      setCoverSrc(avatarUrl(uid, 'cover'))
    }
  }, [userProfile?.user?.profile_picture_url, userProfile?.user?.cover_image_url])

  const uploadImage = async (file, type) => {
    if (!file) return null
    const formData = new FormData()
    formData.append('image', file)
    formData.append('type', type)
    setUploading(true)
    try {
      const res = await fetch(`${urlLocal}/users/upload-avatar`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })
      const data = await res.json()
      if (!res.ok) {
        console.error('Avatar upload failed:', data)
        return null
      }
      // Return null — caller uses the proxy URL directly once DB is updated
      return data.path ? true : null
    } catch (err) {
      console.error('Avatar upload error:', err)
      return null
    } finally {
      setUploading(false)
    }
  }

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    // Show local preview immediately
    const reader = new FileReader()
    reader.onload = (ev) => setProfileImage(ev.target.result)
    reader.readAsDataURL(file)
    const uid = userProfile?.user?.user_id
    const ok = await uploadImage(file, 'profile')
    // Switch from data URL to proxy URL (adds cache-bust so browser re-fetches)
    if (ok && uid) setProfileImage(`${avatarUrl(uid, 'profile')}?t=${Date.now()}`)
  }

  const handleCoverImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => setCoverSrc(ev.target.result)
    reader.readAsDataURL(file)
    const uid = userProfile?.user?.user_id
    const ok = await uploadImage(file, 'cover')
    if (ok && uid) setCoverSrc(`${avatarUrl(uid, 'cover')}?t=${Date.now()}`)
  }

  const firstName = userProfile?.user?.first_name || ''
  const lastName = userProfile?.user?.last_name || ''
  const title = userProfile?.user?.user_title || ''
  const locationText = userProfile?.user?.user_location || ''

  return (
    <motion.div
      className={`${style.container} ${editMode ? style.container_editMode : ''}`}
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
    >
      {/* Glassmorphic background orbs */}
      <div className={style.orb1} />
      <div className={style.orb2} />
      <div className={style.orb3} />

      {/* ---- Hero / Cover card ---- */}
      <motion.div className={style.coverWrapper} variants={fadeUp} custom={0}>
        {/* Cover image or animated gradient */}
        {coverSrc ? (
          <img src={coverSrc} className={style.coverImage} alt="cover" />
        ) : (
          <div className={style.defaultCover} />
        )}
        <div className={style.imageCover} />

        {/* Cover upload button (owner only) */}
        {owner && (
          <>
            <button
              className={style.coverEditBtn}
              onClick={() => coverInputRef.current?.click()}
              disabled={uploading}
            >
              <ImageIcon />
              {uploading ? 'Uploading…' : 'Change cover'}
            </button>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleCoverImageChange}
            />
          </>
        )}

        {/* Avatar circle */}
        <div
          className={style.iconContainer}
          onClick={() => owner && profileInputRef.current?.click()}
          style={{ cursor: owner ? 'pointer' : 'default' }}
        >
          {profileImage ? (
            <img
              src={profileImage}
              className={style.profilePicture}
              alt="profile"
            />
          ) : (
            <svg width={innerWidth < 768 ? 32 : 52} height={innerWidth < 768 ? 32 : 52} viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="8" r="4" fill="rgba(255,255,255,0.9)" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" fill="rgba(255,255,255,0.6)" />
            </svg>
          )}
          {owner && (
            <div className={style.addImage}>
              <CameraIcon />
              <p className={style.addImageLabel}>Upload</p>
            </div>
          )}
        </div>
        {owner && (
          <input
            ref={profileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleProfileImageChange}
          />
        )}

        {/* User info strip */}
        <div className={style.userInfo}>
          <div className={style.userDetails}>
            <p className={style.userName}>
              {firstName} {lastName}
              {owner && editMode && (
                <span
                  onClick={() => toggleModal('profile', userProfile.user)}
                  className={style.profileEdit}
                  title="Edit profile"
                >
                  <EditIcon />
                </span>
              )}
            </p>
            {title && <p className={style.userOccupation}>{title}</p>}
            {locationText && (
              <div className={style.userLocations}>
                <LocationIcon2 width={14} />
                {locationText}
              </div>
            )}
          </div>

          {owner && (
            <div className={style.scoreContainer}>
              <button className={style.button} onClick={editModelToggle}>
                {editMode ? 'Save layout' : 'Edit profile'}
              </button>
            </div>
          )}
        </div>
      </motion.div>

      {/* ---- Profile sections (drag-n-drop layout) ---- */}
      <motion.div variants={fadeUp} custom={1}>
        <ProfileLayout
          editMode={editMode}
          toggleModal={toggleModal}
          openModal={openModal}
        />
      </motion.div>

      {layout?.length <= 0 && (
        <motion.p className={style.emptyProfile} variants={fadeUp} custom={2}>
          {owner ? 'Your' : "This user's"} profile is empty
        </motion.p>
      )}
    </motion.div>
  )
}

export default MainProfile
